import React, { useCallback, useRef } from "react"
import styled from "styled-components"
import { lightGrey, black, yellow, darkBlueBackground } from "./colors"
import { StarIcon } from '@heroicons/react/24/solid'

const Button = styled.button<{
  $isActive: boolean
  $isDisabled?: boolean
  $inverse?: boolean
  $large?: boolean
}>`
  display: flex;
  align-items: center;
  flex-direction: column;
  border-radius: 8px;
  background: ${(props) => (props.$isActive ? lightGrey : "none")};
  padding: 13px;
  border: none;
  color: ${black};
  cursor: pointer;
  opacity: ${(props) => (props.$isDisabled ? "0.35" : "1")};
  transition-property: opacity;
  transition-duration: 0.5s;
  position: relative;

  & > svg {
    width: ${(props) =>
      props.$isDisabled ? "1rem" : props.$large ? "1.5rem" : "1.5rem"};
    height: ${(props) =>
      props.$isDisabled ? "1rem" : props.$large ? "1.5rem" : "1.5rem"};
    transition-property: width, height;
    transition-duration: 0.5s;
  }

  &:active {
    background: ${(props) => (props.$isDisabled ? "none" : lightGrey)};
  }

  -webkit-tap-highlight-color: transparent;
`

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    position: absolute;
    top: 6px;
    right: 3px;
    background: ${darkBlueBackground};
    border-radius: 50%;
    z-index: 1;
`


export const ControlButton: React.FC<{
  icon?: React.ReactNode | null
  isActive?: boolean
  onClick?: () => void
  isDisabled?: boolean
  inverse?: boolean
  large?: boolean
  children?: React.ReactNode
  onlyPro?: boolean
  isPro?: boolean
}> = ({
  icon = null,
  isActive = false,
  onClick = () => {},
  children = [],
  isDisabled = false,
  inverse = false,
  large = false,
  isPro,
  onlyPro
}) => {
  // On iOS Safari / WKWebView the synthesized `click` event is unreliable while
  // the main thread is busy — e.g. during tactic playback the pool canvas redraws
  // ~30x/sec, so a tap on a control flashes the button (`:active` fires on touch)
  // yet `click` never arrives, which is why Pause "did nothing" mid-playback on
  // iPad. Pointer events fire reliably as part of the touch sequence, so we drive
  // the action from `onPointerUp` for touch/pen and keep `onClick` for mouse and
  // keyboard (accessibility). `touchHandledRef` swallows the ghost click that iOS
  // synthesizes after a touch-driven pointerup, so the action runs exactly once.
  const touchHandledRef = useRef<boolean>(false)

  const handlePointerDown = useCallback(() => {
    touchHandledRef.current = false
  }, [])

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (isDisabled || e.pointerType === "mouse") return
      touchHandledRef.current = true
      onClick()
    },
    [isDisabled, onClick]
  )

  const handleClick = useCallback(() => {
    if (isDisabled) return
    // Ignore the ghost click that follows a touch-driven pointerup (handled above).
    if (touchHandledRef.current) {
      touchHandledRef.current = false
      return
    }
    onClick()
  }, [isDisabled, onClick])

  return (
    <Button
      $isActive={isActive}
      $isDisabled={isDisabled}
      $large={large}
      $inverse={inverse}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
    >
      {onlyPro && !isPro && (
        <IconWrapper>
          <StarIcon style={{ width: '12px', color: `${yellow}` }} />
        </IconWrapper>
      )}
      {icon ? icon : children}
    </Button>
  )
}
